import { ProvisionOption } from '@/react/portainer/environments/wizard/EnvironmentsCreationView/WizardK8sInstall/types';
import {
  AuthTypeOption,
  Provider,
} from '@/react/portainer/account/git-credentials/types';

import { K8sDistributionType } from '../../environments/types';

export enum CredentialType {
  CIVO = 'civo',
  LINODE = 'linode',
  DIGITAL_OCEAN = 'digitalocean',
  GOOGLE_CLOUD = 'gke',
  AWS = 'amazon',
  AZURE = 'azure',
  SSH = 'ssh',
  OMNI = 'omni',
  GIT = 'git',
}

export const credentialTitles: Record<CredentialType, string> = {
  civo: 'Civo',
  linode: 'Akamai Connected Cloud',
  digitalocean: 'DigitalOcean',
  gke: 'Google Cloud',
  amazon: 'AWS',
  azure: 'Azure',
  ssh: 'SSH',
  omni: 'Sidero Omni',
  git: 'Git',
};

export const providerToCredentialTypeMap: Record<
  ProvisionOption,
  CredentialType
> = {
  [K8sDistributionType.OMNI]: CredentialType.OMNI,
};

export const credentialTypeHelpLinks: Record<CredentialType, string> = {
  civo: '/admin/settings/credentials/civo',
  linode: '/admin/settings/credentials/linode',
  digitalocean: '/admin/settings/credentials/digitalocean',
  gke: '/admin/settings/credentials/gke',
  amazon: '/admin/settings/credentials/eks',
  azure: '/admin/settings/credentials/aks',
  ssh: '/admin/settings/credentials/ssh',
  omni: '/admin/settings/credentials/omni',
  git: '/admin/settings/credentials/git',
};

export type APICredentials = {
  apiKey: string;
};

export type AccessKeyCredentials = {
  accessKeyId: string;
  secretAccessKey: string;
};

export type ServiceAccountCredentials = {
  jsonKeyBase64: string;
};

export type AzureCredentials = {
  clientID: string;
  clientSecret: string;
  tenantID: string;
  subscriptionID: string;
};

export type SSHCredentials = {
  username: string;
  password: string;
  privateKey: string;
  passphrase: string;
};

export type OmniCredentials = {
  serviceAccountKey: string;
  endpoint: string;
};

export interface OmniFormValues {
  name: string;
  credentials: OmniCredentials;
}

export interface SSHCredentialFormValues {
  name: string;
  credentials: SSHCredentials;
}

export type GitCredentials = {
  username?: string;
  personalAccessToken: string;
  provider: Provider;
  authType: AuthTypeOption;
  urlPattern: string;
};

export interface GitCredentialFormValues {
  name: string;
  credentials: GitCredentials;
}

// GitCredential is the shape of a shared git credential as returned by the
// cloud/gitcredentials API.
export type GitCredential = {
  id: number;
  name: string;
  username: string;
  creationDate: number;
  provider: Provider;
  authorizationType: AuthTypeOption;
  urlPattern: string;
};

export type GitCredentialResponse = {
  gitCredential: GitCredential;
};

export type CredentialDetails =
  | APICredentials
  | AccessKeyCredentials
  | ServiceAccountCredentials
  | AzureCredentials
  | SSHCredentials
  | OmniCredentials
  | GitCredentials;

export type GenericFormValues = {
  name: string;
  credentials: CredentialDetails;
};

export interface CreateCredentialPayload {
  name: string;
  provider: CredentialType;
  credentials: CredentialDetails;
}

export interface UpdateCredentialPayload {
  name?: string;
  provider?: CredentialType;
  credentials?: Partial<CredentialDetails>;
}

// using a type instead of interface to conform to the Record<string, unknown> types required in the datatable component
export type Credential = {
  id: number;
  created: number;
  name: string;
  provider: CredentialType;
  credentials: CredentialDetails;
};
